/**
 * Utility for global warehouse filtering in API requests
 */

/**
 * Appends selected warehouse IDs to URLSearchParams
 * Usage: ?warehouse_ids=1&warehouse_ids=3
 * 
 * @param {URLSearchParams} queryParams - The URLSearchParams object to append to
 * @returns {URLSearchParams} The modified queryParams
 */
export const appendWarehouseParams = (queryParams) => {
    try {
        const saved = sessionStorage.getItem('selected_warehouse_ids');
        if (saved) {
            const ids = JSON.parse(saved);
            if (Array.isArray(ids) && ids.length > 0) {
                ids.forEach(id => {
                    queryParams.append('warehouse_ids', String(id));
                });
            }
        }
    } catch (e) {
        // Silently fail to avoid breaking core API logic
    }
    return queryParams;
};

/**
 * Returns an object with selected warehouse IDs for axios 'params' configuration.
 * Note: Requires custom paramsSerializer in axios to match the exact format ?warehouse_ids=1&warehouse_ids=3
 * 
 * @returns {Object} Params object with warehouse_ids array or empty object
 */
export const getWarehouseParams = () => {
    try {
        const saved = sessionStorage.getItem('selected_warehouse_ids');
        if (saved) {
            const ids = JSON.parse(saved);
            if (Array.isArray(ids) && ids.length > 0) {
                return { warehouse_ids: ids };
            }
        }
    } catch (e) {}
    return {};
};

/**
 * Helper to inject warehouse_ids into a params object directly
 * 
 * @param {Object} params - Existing params object
 * @returns {Object} Merged params object
 */
export const injectWarehouseParams = (params = {}) => {
    try {
        const saved = sessionStorage.getItem('selected_warehouse_ids');
        if (saved) {
            const ids = JSON.parse(saved);
            if (Array.isArray(ids) && ids.length > 0) {
                return { ...params, warehouse_ids: ids };
            }
        }
    } catch (e) {}
    return params;
};


/**
 * Logic to inject warehouse_ids into axios config
 * 
 * @param {Object} config - Axios request config
 * @returns {Object} Modified config
 */
export const applyWarehouseFilter = (config) => {
    // Only apply to GET requests
    if (config.method?.toLowerCase() !== 'get') return config;

    // Allow a caller to opt out of the global filter injection when it manages
    // warehouse_ids itself (e.g. an export whose warehouse selection is
    // authoritative and must not be widened by the SideNav filter).
    if (config.skipWarehouseFilter) return config;

    try {
        const saved = sessionStorage.getItem('selected_warehouse_ids');
        if (saved) {
            const ids = JSON.parse(saved);
            if (Array.isArray(ids) && ids.length > 0) {
                if (!config.params) config.params = {};
                // Inject the global warehouse filter
                config.params.warehouse_ids = ids;
            }
        }
    } catch (e) {}
    
    return config;
};

/**
 * Standard params serializer for warehouse_ids=1&warehouse_ids=2 format
 * 
 * @param {Object} params - Query parameters
 * @returns {string} Serialized query string
 */
export const warehouseParamsSerializer = (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    return searchParams.toString();
};
